// utils/api.js
import axios from "axios";
import { toast } from "react-toastify";

// Create a custom event system for auth errors
export const authEvents = {
  listeners: {},

  // Register listener for auth events
  onAuthError(callback) {
    const id = Date.now().toString();
    this.listeners[id] = callback;

    // Return unsubscribe function
    return () => {
      delete this.listeners[id];
    };
  },

  // Emit auth error event
  emitAuthError() {
    Object.values(this.listeners).forEach((callback) => {
      if (typeof callback === "function") {
        callback();
      }
    });
  },
};

const api = axios.create({
  baseURL: "https://sahmaraby.com/public/api",
  headers: {
    "Content-Type": "application/json",
    "Accept-Language": "ar", // Add this
  },
});

export const getUserData = () => {
  const userData = localStorage.getItem("user");
  if (userData) {
    try {
      return JSON.parse(userData);
    } catch (e) {
      return null;
    }
  }
  return null;
};

// Function to get auth token - this can be called from anywhere
export const getAuthToken = () => {
  const userData = localStorage.getItem("user");
  if (userData) {
    try {
      const { token } = JSON.parse(userData);
      return token;
    } catch (e) {
      return null;
    }
  }
  return null;
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle specific error cases
    if (error.response) {
      // Handle 401 Unauthorized errors
      // if (error.response.status === 401) {
      //   // Check if this is a login attempt (different handling)
      //   if (isLoginAttempt(originalRequest.url)) {
      //     // For login attempts, just show the error message
      //     const errorMessage =
      //       error.response.data.message || "بيانات الاعتماد غير صحيحة";
      //     toast.error(errorMessage);
      //     return Promise.reject(error);
      //   }

      //   // For other 401 errors (session expired etc.)
      //   if (!originalRequest._retry) {
      //     originalRequest._retry = true;
      //     // Clear user data but don't redirect with window.location
      //     localStorage.removeItem("user");

      //     // Instead, emit an event that components can listen for
      //     authEvents.emitAuthError();

      //     toast.error("انتهت جلستك. يرجى تسجيل الدخول مرة أخرى.");
      //     return Promise.reject(error);
      //   }

      //   window.location.href = "/login";
      // }

      // Handle 403 Forbidden errors
      if (error.response.status === 403) {
        toast.error("ليس لديك إذن لتنفيذ هذا الإجراء");
        return Promise.reject(error);
      }

      // Handle 404 Not Found errors
      if (error.response.status === 404) {
        toast.error("لم يتم العثور على المورد المطلوب");
        return Promise.reject(error);
      }

      // Handle 422 Validation errors
      if (error.response.status === 422) {
        const errors = error.response.data.errors;
        if (errors && typeof errors === "object") {
          Object.keys(errors).forEach((key) => {
            if (Array.isArray(errors[key]) && errors[key][0]) {
              toast.error(errors[key][0]);
            }
          });
        } else {
          toast.error(error.response.data.message || "خطأ في التحقق من الصحة");
        }
        return Promise.reject(error);
      }

      // Handle 500 Server errors
      if (error.response.status >= 500) {
        toast.error("حدث خطأ غير متوقع. الرجاء معاودة المحاولة في وقت لاحق.");
        return Promise.reject(error);
      }
    }

    // Handle network errors
    if (error.message === "Network Error") {
      toast.error(
        "غير قادر على الاتصال بالخادم. يرجى التحقق من اتصال الإنترنت الخاص بك.",
      );
      return Promise.reject(error);
    }

    // Handle request cancellation
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // Handle all other errors
    toast.error(error.response?.data?.message || "حدث خطأ غير متوقع");
    return Promise.reject(error);
  },
);

// Helper methods for common API operations
export const apiHelpers = {
  handleError: (error) => {
    if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else {
      toast.error("حدث خطأ غير متوقع");
    }
    return Promise.reject(error);
  },

  // Add more helper methods as needed
  isAuthError: (error) => {
    return error.response?.status === 401;
  },

  isValidationError: (error) => {
    return error.response?.status === 422;
  },
};

export default api;
